import { useCallback, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { ShoppingCart, AlertCircle, RefreshCw, X } from "lucide-react";
import { startOfMonth, endOfMonth, format, parseISO, isValid } from "date-fns";
import { toast } from "sonner";
import {
  parseAsString,
  parseAsInteger,
  parseAsStringLiteral,
  useQueryState,
  useQueryStates,
} from "nuqs";
import { Button } from "@cieda/djayma-ui";
import AdminLayout from "@/components/admin/AdminLayout";
import { useAdminPageTitle } from "@/hooks/admin/useAdminPageTitle";
import {
  SearchInput,
  ExportButton,
  BulkActionBar,
  DateRangePicker,
  type DateRange,
  type SortState,
} from "@/components/admin/shared";
import { OrdersTable } from "@/components/admin/orders/OrdersTable";
import {
  OrderFilters,
  type OrderFilterValues,
} from "@/components/admin/orders/OrderFilters";
import { useOrdersList, useBulkUpdateOrderStatus } from "@/hooks/admin/useOrders";
import {
  ORDER_SORT_COLUMNS,
  type OrderSummary,
  type BulkStatusTarget,
  type SortDirection,
  type OrderSortColumn,
} from "@/lib/admin-api/orders";

// ---------------------------------------------------------------------------
// Constants
// ---------------------------------------------------------------------------

const PAGE_SIZE = 20;

const EXPORT_COLUMNS = [
  { key: "reference", label: "Référence" },
  { key: "customer_name", label: "Client" },
  { key: "total_ttc", label: "Montant TTC (FCFA)" },
  { key: "status", label: "Statut" },
  { key: "payment_method", label: "Paiement" },
  { key: "created_at", label: "Date" },
] as const;

const SORT_DIRECTIONS = ["asc", "desc"] as const;

// Local state shape for selection; intentionally NOT URL-synced (selection is ephemeral)
// ---------------------------------------------------------------------------

// ---------------------------------------------------------------------------
// URL-synced state parsers
// ---------------------------------------------------------------------------

function defaultFromISO(): string {
  return format(startOfMonth(new Date()), "yyyy-MM-dd");
}

function defaultToISO(): string {
  return format(endOfMonth(new Date()), "yyyy-MM-dd");
}

function parseISODateOrDefault(value: string | null, fallback: Date): Date {
  if (!value) return fallback;
  const d = parseISO(value);
  return isValid(d) ? d : fallback;
}

// ---------------------------------------------------------------------------
// Page component
// ---------------------------------------------------------------------------

export default function Orders() {
  const { t } = useTranslation("admin");
  useAdminPageTitle("Commandes");

  // Selection lives in local state only — it must NOT appear in the URL.
  // Audit S-H12: previously this used useQueryState which leaked order UUIDs
  // into browser history, server access logs, and referrer headers.
  const [selectedIds, setSelectedIds] = useState<string>("");
  const selectedIdsList = useMemo(
    () => (selectedIds ? selectedIds.split(",").filter(Boolean) : []),
    [selectedIds],
  );

  // URL-synced filters, search, pagination, date range, and sort
  const [urlState, setUrlState] = useQueryStates(
    {
      page: parseAsInteger.withDefault(1),
      q: parseAsString.withDefault(""),
      status: parseAsString.withDefault(""),
      payment_method: parseAsString.withDefault(""),
      region_id: parseAsString.withDefault(""),
      date_from: parseAsString.withDefault(defaultFromISO()),
      date_to: parseAsString.withDefault(defaultToISO()),
      sort: parseAsStringLiteral(ORDER_SORT_COLUMNS).withDefault("created_at"),
      direction: parseAsStringLiteral(SORT_DIRECTIONS).withDefault("desc"),
    },
    { clearOnDefault: true, history: "replace" },
  );

  const {
    page,
    q: search,
    status,
    payment_method,
    region_id,
    date_from,
    date_to,
    sort,
    direction,
  } = urlState;

  // Derived
  const filters: OrderFilterValues = useMemo(
    () => ({
      status: status || null,
      payment_method: payment_method || null,
      region_id: region_id || null,
    }),
    [status, payment_method, region_id],
  );

  const dateRange: DateRange = useMemo(
    () => ({
      from: parseISODateOrDefault(date_from, startOfMonth(new Date())),
      to: parseISODateOrDefault(date_to, endOfMonth(new Date())),
    }),
    [date_from, date_to],
  );

  const hasActiveFilters =
    Boolean(search) ||
    Boolean(status) ||
    Boolean(payment_method) ||
    Boolean(region_id);

  // Query params sent to API
  const queryParams = useMemo(
    () => ({
      page,
      limit: PAGE_SIZE,
      status: status || null,
      payment_method: payment_method || null,
      region_id: region_id || null,
      date_from,
      date_to,
      q: search || null,
      sort,
      direction,
    }),
    [
      page,
      status,
      payment_method,
      region_id,
      date_from,
      date_to,
      search,
      sort,
      direction,
    ],
  );

  const { data, isLoading, isError, refetch, isRefetching } = useOrdersList(queryParams);
  const bulkUpdate = useBulkUpdateOrderStatus();

  // ---------------------------------------------------------------------------
  // Handlers
  // ---------------------------------------------------------------------------

  const handleFilterChange = useCallback(
    (key: string, value: unknown) => {
      const v = value == null ? "" : String(value);
      switch (key) {
        case "status":
          void setUrlState({ status: v, page: 1 });
          break;
        case "payment_method":
          void setUrlState({ payment_method: v, page: 1 });
          break;
        case "region_id":
          void setUrlState({ region_id: v, page: 1 });
          break;
      }
    },
    [setUrlState],
  );

  const handleSearch = useCallback(
    (value: string) => {
      void setUrlState({ q: value, page: 1 });
    },
    [setUrlState],
  );

  const handleDateChange = useCallback(
    (range: DateRange) => {
      void setUrlState({
        date_from: format(range.from, "yyyy-MM-dd"),
        date_to: format(range.to, "yyyy-MM-dd"),
        page: 1,
      });
    },
    [setUrlState],
  );

  const handleSort = useCallback(
    (nextSort: SortState) => {
      const col = ORDER_SORT_COLUMNS.includes(nextSort.column as OrderSortColumn)
        ? (nextSort.column as OrderSortColumn)
        : "created_at";
      const dir: SortDirection = nextSort.direction;
      void setUrlState({ sort: col, direction: dir, page: 1 });
    },
    [setUrlState],
  );

  const handlePageChange = useCallback(
    (nextPage: number) => {
      void setUrlState({ page: nextPage });
    },
    [setUrlState],
  );

  const handleRowSelect = useCallback(
    (ids: readonly string[]) => {
      setSelectedIds(ids.length > 0 ? ids.join(",") : "");
    },
    [setSelectedIds],
  );

  const handleClearSelection = useCallback(() => {
    setSelectedIds("");
  }, [setSelectedIds]);

  const handleClearFilters = useCallback(() => {
    void setUrlState({
      q: "",
      status: "",
      payment_method: "",
      region_id: "",
      page: 1,
    });
  }, [setUrlState]);

  const runBulkTransition = useCallback(
    (newStatus: BulkStatusTarget, successKey: string) => {
      if (selectedIdsList.length === 0) return;
      bulkUpdate.mutate(
        { orderIds: selectedIdsList, newStatus },
        {
          onSuccess: (res) => {
            void setSelectedIds("");
            if (res.updated > 0) {
              toast.success(
                t(successKey, { count: res.updated, defaultValue: "{{count}} commandes mises à jour" }),
              );
            }
            if (res.failed.length > 0) {
              toast.error(
                t("orders.bulk_partial_failure", {
                  count: res.failed.length,
                  defaultValue: "{{count}} commandes n'ont pas pu être mises à jour",
                }),
              );
            }
          },
          onError: () => {
            toast.error(
              t("orders.bulk_error", "Impossible de mettre à jour les commandes."),
            );
          },
        },
      );
    },
    [selectedIdsList, bulkUpdate, setSelectedIds, t],
  );

  const handleBulkConfirm = useCallback(() => {
    runBulkTransition("confirmed", "orders.bulk_confirmed");
  }, [runBulkTransition]);

  // S-H10: extend bulk-toolbar with the transitions the backend accepts
  // (confirmed / shipped / delivered / cancelled — see BULK_STATUS_TARGETS
  // in lib/admin-api/orders.ts). "processing" is NOT a valid bulk target
  // even though it's a legitimate order status; admins move confirmed →
  // shipped directly, or use the per-row transition for processing.
  const handleBulkShipped = useCallback(() => {
    runBulkTransition("shipped", "orders.bulk_shipped");
  }, [runBulkTransition]);

  const handleBulkDelivered = useCallback(() => {
    runBulkTransition("delivered", "orders.bulk_delivered");
  }, [runBulkTransition]);

  const handleBulkCancel = useCallback(() => {
    runBulkTransition("cancelled", "orders.bulk_cancelled");
  }, [runBulkTransition]);

  // ---------------------------------------------------------------------------
  // Derived data
  // ---------------------------------------------------------------------------

  const orders = data?.orders ?? [];
  const pagination = data?.pagination ?? { page: 1, limit: PAGE_SIZE, total: 0 };

  const currentSort: SortState = { column: sort, direction };

  // Export data: shape matches EXPORT_COLUMNS
  const exportData = useMemo(
    () =>
      orders.map((o: OrderSummary) => ({
        reference: o.reference,
        customer_name: o.customer_name,
        total_ttc: o.total_ttc,
        status: o.status,
        payment_method: o.payment_method,
        created_at: o.created_at,
      })),
    [orders],
  );

  return (
    <AdminLayout>
      <div className="space-y-6">
        {/* Page header */}
        <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
          <div className="flex items-center gap-3">
            <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-amber-100 text-amber-700">
              <ShoppingCart className="h-5 w-5" />
            </div>
            <div>
              <h1 className="font-display text-xl font-semibold text-stone-900">
                {t("orders.title", "Commandes")}
              </h1>
              <p className="text-sm text-stone-500">
                {t("orders.subtitle", "Gérez les commandes de vos clients")}
              </p>
            </div>
          </div>

          <ExportButton
            data={exportData}
            columns={[...EXPORT_COLUMNS]}
            filename="commandes-djayma"
          />
        </div>

        {/* Toolbar: search + filters + date range */}
        <div className="space-y-3">
          <div className="flex flex-col gap-3 sm:flex-row sm:items-center">
            <div className="w-full sm:max-w-xs">
              <SearchInput
                value={search}
                onChange={handleSearch}
                placeholder={t(
                  "orders.search_placeholder",
                  "Rechercher par réf, client, téléphone...",
                )}
              />
            </div>
            <DateRangePicker value={dateRange} onChange={handleDateChange} />
          </div>

          <div className="flex flex-wrap items-center gap-3">
            <OrderFilters values={filters} onChange={handleFilterChange} />
            {hasActiveFilters && (
              <Button
                variant="ghost"
                size="sm"
                onClick={handleClearFilters}
                className="gap-1.5 text-stone-500 hover:text-stone-700"
              >
                <X className="h-4 w-4" />
                {t("orders.clear_filters", "Effacer les filtres")}
              </Button>
            )}
          </div>

          {/* Result count */}
          {!isLoading && !isError && (
            <p className="text-xs text-stone-500" aria-live="polite">
              {t("orders.result_count", {
                count: pagination.total,
                defaultValue: "{{count}} commandes trouvées",
              })}
            </p>
          )}
        </div>

        {/* Error state with retry */}
        {isError && (
          <div className="flex items-start gap-3 rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">
            <AlertCircle className="mt-0.5 h-4 w-4 shrink-0" />
            <div className="flex-1">
              <p className="font-medium">
                {t("orders.error_loading", "Impossible de charger les commandes.")}
              </p>
              <p className="mt-0.5 text-red-600/80">
                {t("orders.error_hint", "Vérifiez votre connexion et réessayez.")}
              </p>
            </div>
            <Button
              variant="outline"
              size="sm"
              onClick={() => void refetch()}
              disabled={isRefetching}
              className="gap-1.5 border-red-200 text-red-700 hover:bg-red-100"
            >
              <RefreshCw className={`h-3.5 w-3.5 ${isRefetching ? "animate-spin" : ""}`} />
              {t("orders.retry", "Réessayer")}
            </Button>
          </div>
        )}

        {/* Table */}
        <OrdersTable
          data={orders}
          pagination={pagination}
          isLoading={isLoading}
          onPageChange={handlePageChange}
          onSort={handleSort}
          onRowSelect={handleRowSelect}
          currentSort={currentSort}
        />

        {/* Bulk action bar — covers every legitimate status transition so
            admins can operate on batches without per-row clicking. */}
        <BulkActionBar
          selectedCount={selectedIdsList.length}
          onClear={handleClearSelection}
          actions={[
            {
              label: t("orders.bulk_confirm", "Confirmer"),
              onClick: handleBulkConfirm,
            },
            {
              label: t("orders.bulk_shipped", "Expédier"),
              onClick: handleBulkShipped,
            },
            {
              label: t("orders.bulk_delivered", "Livrer"),
              onClick: handleBulkDelivered,
            },
            {
              label: t("orders.bulk_cancel", "Annuler"),
              onClick: handleBulkCancel,
              variant: "destructive",
            },
          ]}
        />
      </div>
    </AdminLayout>
  );
}
